import type { SchemaVideoShareInfoResult } from 'api'

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value !== ''

/**
 * 組出對外分享連結。
 *
 * 使用 `URL` + `searchParams` 而非模板字串，以正確處理跳脫，
 * 並保留 base 本身可能已帶的 query。
 *
 * @returns 組裝後的 URL；base 無法解析時回傳 null（由呼叫端降級）
 */
export const buildShareUrl = (base: string, videoShare: string, encryptedCloudFront: string): string | null => {
  let url: URL
  try {
    url = new URL(base)
  } catch {
    return null
  }
  url.searchParams.set('data', videoShare)
  url.searchParams.set('cf', encryptedCloudFront)
  return url.toString()
}

/**
 * 分享回應是否可用於產出對外連結。
 *
 * `VideoShare` 與 `cloudfront` 都是後端加密後的字串，前端只負責原樣透傳，
 * 不解密、不解析、不判斷簽章是否到期（由落地頁與 CloudFront 負責）。
 *
 * 型別上兩者皆為必填，但 API 文件明載 cloudfront「未帶 resource 時不回傳」，
 * 且後端 rollout 未完成時回應可能缺欄位，故一律做 runtime 檢查：
 * 任一條件不成立即 fail-closed 降級，避免產出對外必然被 CloudFront 擋下的連結。
 */
export const isUsableShareResponse = (
  response: SchemaVideoShareInfoResult
): response is SchemaVideoShareInfoResult & { VideoShare: string; cloudfront: string } => {
  return (
    response.haveH265 === true && isNonEmptyString(response.VideoShare) && isNonEmptyString(response.cloudfront)
  )
}
